import {
  resendCode as regenerateCode,
  updateEmail as update,
  verifyEmail as verify,
} from '@rover/react-lib/src/apis/emailVerification.api';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck/createDuck';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck/types';
import type { EmailVerification } from '@rover/types/src/EmailVerification';
import APIError from '@rover/utilities/APIError';
import type { ApiErrorMessage } from '@rover/utilities/apiErrorMessage';
import { toApiErrorMessage } from '@rover/utilities/apiErrorMessage';

type Actions = {
  resendCodePending: ActionCreator<void>;
  resendCodeSuccess: ActionCreator<void>;
  resendCodeFailure: ActionCreator<{
    message: ApiErrorMessage;
    status?: number;
  }>;
  verifyEmailPending: ActionCreator<void>;
  verifyEmailSuccess: ActionCreator<void>;
  verifyEmailFailure: ActionCreator<{
    message: ApiErrorMessage;
    status?: number;
  }>;
  updateEmailPending: ActionCreator<void>;
  updateEmailSuccess: ActionCreator<void>;
  updateEmailFailure: ActionCreator<{
    message: ApiErrorMessage;
    status?: number;
  }>;
  resetState: ActionCreator<void>;
};

type Effects = {
  resendCode: EffectCreator<boolean | undefined>;
  verifyEmail: EffectCreator<EmailVerification>;
  updateEmail: EffectCreator<string>;
};

type Selectors = {
  getIsSubmitting: SelectorCreator<boolean>;
  getVerifyStatus: SelectorCreator<number | null>;
  getResendStatus: SelectorCreator<number | null>;
  getUpdateStatus: SelectorCreator<number | null>;
  getErrorMessage: SelectorCreator<ApiErrorMessage | null>;
};

type State = {
  /** Unresolved, for the container holding the request's `i18n`. `null` is "nothing wrong". */
  errorMessage: ApiErrorMessage | null;
  resendStatus: number | null;
  verifyStatus: number | null;
  updateStatus: number | null;
  submitting: boolean;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'emailVerification',
  initialState: {
    errorMessage: null,
    resendStatus: null,
    verifyStatus: null,
    updateStatus: null,
    submitting: false,
  },
  actions: {
    resendCodePending: () => (state) => ({ ...state, submitting: true }),
    resendCodeSuccess: () => (state) => {
      return {
        ...state,
        submitting: false,
        errorMessage: null,
        resendStatus: 200,
      };
    },
    resendCodeFailure:
      ({ message, status }) =>
      (state) => ({
        ...state,
        submitting: false,
        errorMessage: message,
        resendStatus: status ?? null,
      }),
    verifyEmailPending: () => (state) => ({ ...state, errorMessage: null, submitting: true }),
    verifyEmailSuccess: () => (state) => ({
      ...state,
      errorMessage: null,
      submitting: false,
      verifyStatus: 200,
    }),
    verifyEmailFailure:
      ({ message, status }) =>
      (state) => ({
        ...state,
        submitting: false,
        errorMessage: message,
        verifyStatus: status ?? null,
      }),
    updateEmailPending: () => (state) => ({ ...state, submitting: true }),
    updateEmailSuccess: () => (state) => ({
      ...state,
      errorMessage: null,
      submitting: false,
      updateStatus: 200,
    }),
    updateEmailFailure:
      ({ message, status }) =>
      (state) => ({
        ...state,
        submitting: false,
        errorMessage: message,
        updateStatus: status ?? null,
      }),
    resetState: () => () => ({
      submitting: false,
      errorMessage: null,
      resendStatus: null,
      verifyStatus: null,
      updateStatus: null,
    }),
  },
  effects: {
    resendCode: (refresh?: boolean) => async (dispatch, getState, duckActions) => {
      dispatch(duckActions.resendCodePending());
      try {
        await regenerateCode(refresh);
        dispatch(duckActions.resendCodeSuccess());
      } catch (error) {
        const { status } = error as APIError;
        dispatch(duckActions.resendCodeFailure({ message: toApiErrorMessage(error), status }));
      }
    },
    verifyEmail:
      ({ code, emailVerificationStatus }: EmailVerification) =>
      async (dispatch, getState, duckActions) => {
        dispatch(duckActions.verifyEmailPending());

        try {
          await verify(code, emailVerificationStatus);
          dispatch(duckActions.verifyEmailSuccess());
        } catch (error) {
          const { status } = error as APIError;
          dispatch(duckActions.verifyEmailFailure({ message: toApiErrorMessage(error), status }));
        }
      },
    updateEmail: (email: string) => async (dispatch, getState, duckActions) => {
      dispatch(duckActions.updateEmailPending());
      try {
        await update(email);
        dispatch(duckActions.updateEmailSuccess());
      } catch (error) {
        const { status } = error as APIError;
        dispatch(duckActions.updateEmailFailure({ message: toApiErrorMessage(error), status }));
      }
    },
  },
  selectors: {
    getIsSubmitting: (getState, createSelector) =>
      createSelector([getState], (state) => {
        return state.submitting;
      }),
    getVerifyStatus: (getState, createSelector) =>
      createSelector([getState], (state) => {
        return state.verifyStatus;
      }),
    getResendStatus: (getState, createSelector) =>
      createSelector([getState], (state) => {
        return state.resendStatus;
      }),
    getUpdateStatus: (getState, createSelector) =>
      createSelector([getState], (state) => {
        return state.updateStatus;
      }),
    getErrorMessage: (getState, createSelector) =>
      createSelector([getState], (state) => {
        return state.errorMessage;
      }),
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
